// OwnerHQ inner panel: XP rules editor -- every XP-earning action, grouped and editable.
import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const GROUPS = [
  {
    title: "Referrals · free tier",
    keys: [
      ["refer_free_tier", "Refer a free-tier signup"],
      ["refer_free_milestone_1", "Milestone: 1 free-tier referral"],
      ["refer_free_milestone_3", "Milestone: 3 free-tier referrals"],
      ["refer_free_milestone_5", "Milestone: 5 free-tier referrals"],
      ["refer_free_milestone_10", "Milestone: 10 free-tier referrals"],
      ["refer_free_milestone_50", "Milestone: 50 free-tier referrals"],
      ["refer_free_milestone_100", "Milestone: 100 free-tier referrals"],
    ],
  },
  {
    title: "Referrals · Studio tier",
    keys: [
      ["refer_studio_tier", "Refer a Studio-tier purchase"],
      ["refer_studio_milestone_1", "Milestone: 1 Studio referral"],
      ["refer_studio_milestone_3", "Milestone: 3 Studio referrals"],
      ["refer_studio_milestone_5", "Milestone: 5 Studio referrals"],
      ["refer_studio_milestone_10", "Milestone: 10 Studio referrals"],
      ["refer_studio_milestone_15", "Milestone: 15 Studio referrals"],
      ["refer_studio_milestone_20", "Milestone: 20 Studio referrals"],
      ["refer_studio_milestone_25", "Milestone: 25 Studio referrals"],
      ["refer_studio_milestone_50", "Milestone: 50 Studio referrals"],
    ],
  },
  {
    title: "Stickers",
    keys: [
      ["send_free_sticker", "Send a free sticker"],
      ["send_paid_sticker", "Send a paid sticker"],
      ["send_sticker_500nx", "Send a sticker worth 500+ NX"],
      ["send_sticker_1000nx", "Send a sticker worth 1000+ NX"],
      ["send_sticker_5000nx", "Send a sticker worth 5000+ NX"],
      ["send_sticker_10000nx", "Send a sticker worth 10000+ NX"],
    ],
  },
  {
    title: "Supporter subscriptions",
    keys: [
      ["become_supporter", "Become a supporter of a creator"],
      ["become_supporter_3_in_month", "Milestone: support 3 different creators in a month"],
    ],
  },
  {
    title: "Going live",
    keys: [
      ["go_live_mobile", "Go live from mobile"],
      ["go_live_obs", "Go live from OBS Studio"],
    ],
  },
  {
    title: "Profile & onboarding",
    keys: [
      ["upload_profile_pic", "Upload a profile picture"],
      ["upload_logo", "Upload a channel logo"],
      ["create_bio", "Write a bio"],
      ["create_first_post", "Create first post"],
      ["create_first_story", "Create first story"],
    ],
  },
  {
    title: "Social",
    keys: [
      ["add_ed3n_friend", "Add ED3N as a friend"],
      ["follow_ed3n", "Follow ED3N"],
      ["follow_nexus_live", "Follow NEXUS Live"],
      ["follow_elysium_wear", "Follow Elysium Wear"],
      ["follow_sponsored_account", "Follow a sponsored account"],
      ["add_friend_accepted", "Friend request accepted"],
      ["follow_any_user", "Follow any user"],
      ["first_video_call", "First video call"],
      ["first_snap", "Send first Snap"],
    ],
  },
];

export default function OwnerXPTab() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/xp_rules");
      setRules(data);
    } catch {
      toast.error("Failed to load XP rules");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const patch = Object.fromEntries(
        Object.entries(rules).map(([k, v]) => [k, Number(v) || 0])
      );
      const { data } = await api.post("/owner/xp_rules", patch);
      setRules(data);
      toast.success("XP rules saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading || !rules) return <div className="text-white/40 text-sm">Loading XP rules…</div>;

  return (
    <div className="space-y-6" data-testid="owner-xp-tab">
      <div>
        <div className="label-eyebrow text-nexus-cyan">// xp &amp; leveling · owner controls</div>
        <h3 className="font-display font-black text-2xl mt-1">How XP is earned.</h3>
        <p className="text-xs text-white/60 mt-1">
          Every action below awards this many XP the moment it happens. Leveling curve itself
          (how much XP each level needs) is not editable here — only what earns XP.
        </p>
      </div>

      {GROUPS.map((group) => (
        <section key={group.title} className="border border-white/10 bg-nexus-surface p-4 space-y-3">
          <div className="label-eyebrow">// {group.title}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.keys.map(([key, label]) => (
              <div key={key} className="flex items-center gap-2 text-sm" data-testid={`xp-field-${key}`}>
                <span className="text-white/60 flex-1">{label}</span>
                <input
                  value={rules[key] ?? 0}
                  onChange={(e) => setRules({ ...rules, [key]: e.target.value })}
                  type="number"
                  min="0"
                  className="bg-transparent border border-white/10 px-2 py-1.5 text-sm w-24"
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      <button onClick={save} disabled={saving} className="btn-nexus" data-testid="xp-save">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save XP rules"}
      </button>
    </div>
  );
}
